import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieService from "../../context/cookies";
import { base } from "../../constants/apis";
import { ApiResponse } from "../../types/ApiResponse";
import {
  PointsOfInterestData,
  ProcurementAddChatRes,
  Thread,
} from "../../types/ProcurementChat";
import { toast } from "react-toastify";

export const procurementAIApi = createApi({
  reducerPath: "procurementAIApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis/procurement_chats/`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("jwtToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["procuremetThreads"],

  endpoints: (builder) => ({
    getProcuremetThreads: builder.query<Thread[], void>({
      query: () => ({
        url: "/get.php",
        method: "POST",
      }),
      transformResponse: (res: ApiResponse<Thread[]>) => res.data,
      providesTags: ["procuremetThreads"],
    }),

    sendChatMessage: builder.mutation<ProcurementAddChatRes, any>({
      query: (payload) => ({
        url: "/add.php",
        method: "POST",
        body: payload,
      }),

      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const tempThreadId = payload.temp_thread_id ?? payload.thread_id;

        const optimisticPatch = dispatch(
          procurementAIApi.util.updateQueryData(
            "getProcuremetThreads",
            undefined,
            (draft) => {
              let thread = draft.find((t) => t.thread_id === tempThreadId);

              const optimisticMessage = {
                id: Date.now(),
                uid: payload.uid,
                thread_id: tempThreadId,
                user_chat: payload.user_chat,
                ai_chat: null,
                created_at: new Date().toISOString(),
                isOptimistic: true,
              };

              if (thread) {
                thread.messages.push(optimisticMessage);
              } else {
                draft.push({
                  thread_id: tempThreadId,
                  messages: [optimisticMessage],
                });
              }
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;

          dispatch(
            procurementAIApi.util.updateQueryData(
              "getProcuremetThreads",
              undefined,
              (draft) => {
                const thread = draft.find((t) => t.thread_id === tempThreadId);

                if (!thread) return;

                if (data.status === "success") {
                  thread.thread_id = data.thread_id;

                  thread.messages.push({
                    id: data.chart_id,
                    uid: payload.uid,
                    thread_id: data.thread_id,
                    user_chat: null,
                    ai_chat: data.trade_agent?.success
                      ? data.trade_agent.html_output
                      : data.ai_response,
                    created_at: new Date().toISOString(),
                  });
                }
              },
            ),
          );

          // update the points of interest cache
          dispatch(
            procurementAIApi.util.updateQueryData(
              "getPointsOfInterest",
              { thread_id: payload.thread_id }, // ✅ must match
              (draft) => {
                if (!draft) return;

                if (data.status === "success") {
                  draft.interests_json = data.points_of_interest_answers;
                }
              },
            ),
          );
        } catch {
          optimisticPatch.undo();
        }
      },
    }),

    deleteProcuremetThreads: builder.mutation({
      query: (payload) => ({
        url: "/delete.php",
        method: "POST",
        body: payload, // { thread_id }
      }),

      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        // Optimistically remove from threads list
        const patchResult = dispatch(
          procurementAIApi.util.updateQueryData(
            "getProcuremetThreads",
            undefined,
            (draft) => {
              if (!draft) return;

              return draft.filter(
                (thread) => thread.thread_id !== payload.thread_id,
              );
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;

          if (data?.status === "success") {
            toast.success("Thread deleted successfully");
          } else {
            // ❗ API responded but failed logically
            patchResult.undo();
            toast.error(data?.message || "Failed to delete thread");
          }
        } catch {
          // rollback if API fails
          patchResult.undo();
          toast.error("Something went wrong while deleting");
        }
      },
    }),

    getPointsOfInterest: builder.query({
      query: (payload) => ({
        url: "/points_of_interest.php",
        method: "POST",
        body: payload,
      }),
      transformResponse: (res: ApiResponse<PointsOfInterestData>) => {
        if (res.status === "success") return res.data;
      },
    }),
  }),
});

export const {
  useGetProcuremetThreadsQuery,
  useSendChatMessageMutation,
  useGetPointsOfInterestQuery,
  useDeleteProcuremetThreadsMutation,
} = procurementAIApi;
